/**
 * WordPress dependencies
 */
import { __, sprintf } from '@wordpress/i18n';
import { Component, Fragment, RawHTML } from '@wordpress/element';
import { compose } from '@wordpress/compose';
import { Modal, Spinner, TabPanel, SelectControl, Tooltip, ExternalLink, Button, Icon } from '@wordpress/components';
import { withDispatch, withSelect } from '@wordpress/data';
import { parse } from '@wordpress/blocks';
import apiFetch from '@wordpress/api-fetch';

/**
 * External dependencies.
 */
import classnames from 'classnames';
import Masonry from 'react-masonry-component';
import LazyLoad from 'react-lazyload';

class TemplatesModal extends Component {
	constructor( props ) {
		super( props );

		this.state = {
			loading: false,
			activeCategory: {},
			error: false,
			templates: false,
		};

		this.getSelectedCategory = this.getSelectedCategory.bind( this );
		this.printCategorySelect = this.printCategorySelect.bind( this );
		this.getTemplates = this.getTemplates.bind( this );
	}

	componentDidMount() {
		this.props.getTemplates().then( ( res ) => {
			this.setState( ( prevState ) => ( {
				...prevState,
				templates: res,
			} ) );
		} );
	}

	getSelectedCategory( type ) {
		return this.state.activeCategory[ type ] || false;
	}

	getTemplates( type, categorySelected = null ) {
		const { templates } = this.state;

		if ( ! templates ) {
			return templates;
		}
		const result = [];

		categorySelected =
			null === categorySelected ? this.getSelectedCategory( type ) : '';

		templates.forEach( ( template ) => {
			let allow = ! type;

			// type check.
			if ( ! allow && template.types ) {
				template.types.forEach( ( typeData ) => {
					if ( typeData.slug && type === typeData.slug ) {
						allow = true;
					}
				} );
			}

			// category check.
			if ( allow && categorySelected && template.categories ) {
				let categoryAllow = false;
				template.categories.forEach( ( catData ) => {
					if ( catData.slug && categorySelected === catData.slug ) {
						categoryAllow = true;
					}
				} );
				allow = categoryAllow;
			}

			if ( allow ) {
				result.push( template );
			}
		} );

		return result;
	}

	printCategorySelect( type ) {
		const templates = this.getTemplates( type, '' );
		const categories = {};
		const selectData = [];

		templates.forEach( ( template ) => {
			if ( template.categories && template.categories.length ) {
				template.categories.forEach( ( catData ) => {
					if ( ! categories[ catData.slug ] ) {
						categories[ catData.slug ] = true;
						selectData.push( {
							value: catData.slug,
							label: catData.name,
						} );
					}
				} );
			}
		} );

		if ( selectData.length ) {
			selectData.unshift( {
				value: '',
				label: __( '-- Select Category --', '@@text_domain' ),
			} );
			return (
				<SelectControl
					value={ this.getSelectedCategory( type ) }
					options={ selectData }
					onChange={ ( value ) => {
						this.setState( ( prevState ) => ( {
							activeCategory: {
								...prevState.activeCategory,
								...{
									[ type ]: value,
								},
							},
						} ) );
					} }
				/>
			);
		}

		return null;
	}

	render() {
		const { insertTemplate, getTemplateData, onRequestClose } = this.props;

		const allTemplates = this.getTemplates();
		const themeTemplates = this.getTemplates( 'theme' );

		const showLoadingSpinner =
			this.state.loading || ! allTemplates || ! allTemplates.length;

		return (
			<Modal
				className={ classnames(
					'gutenify-plugin-templates-modal gutenify-plugin-templates-modal-hide-header',
					showLoadingSpinner ? 'gutenify-plugin-templates-modal-loading' : ''
				) }
				position="top"
				size="lg"
				onRequestClose={ () => {
					onRequestClose();
				} }
				shouldCloseOnClickOutside={ false }
				// icon={ getIcon( 'plugin-templates' ) }
			>
				<div className="components-modal__header">
					<div className="components-modal__header-heading-container">
						<h1
							id="components-modal-header-1"
							className="components-modal__header-heading"
						>
							{ __( 'Templates', '@@text_domain' ) }
						</h1>
						<Button variant="secondary" onClick={ () => {
							this.setState( ( prevState ) => ( {
								...prevState,
								loading: true,
							} ) );
							this.props.getTemplates( true ).then( ( res ) => {
								this.setState( ( prevState ) => ( {
									...prevState,
									templates: res,
									loading: false,
								} ) );
							} );
						} }><Icon icon="update" /></Button>
					</div>
					{ showLoadingSpinner ? (
						<div className="gutenify-plugin-templates-modal-loading-spinner">
							<Spinner />
						</div>
					) : (
						''
					) }
					<button
						type="button"
						aria-label="Close dialog"
						className="components-button components-icon-button"
						onClick={ () => {
							onRequestClose();
						} }
					>
						<svg
							aria-hidden="true"
							role="img"
							focusable="false"
							className="dashicon dashicons-no-alt"
							xmlns="http://www.w3.org/2000/svg"
							width="20"
							height="20"
							viewBox="0 0 20 20"
						>
							<path d="M14.95 6.46L11.41 10l3.54 3.54-1.41 1.41L10 11.42l-3.53 3.53-1.42-1.42L8.58 10 5.05 6.47l1.42-1.42L10 8.58l3.54-3.53z" />
						</svg>
					</button>
				</div>

				{ allTemplates && allTemplates.length ? (
					<TabPanel
						className="gutenify-control-tabs gutenify-component-modal-tab-panel"
						tabs={ [
							...( themeTemplates && themeTemplates.length
								? [
									{
										name: 'theme',
										title: (
											<Tooltip
												text={ __(
													'Templates from the theme.',
													'@@text_domain'
												) }
											>
												<span>
													{ __( 'Theme', '@@text_domain' ) }
												</span>
											</Tooltip>
										),
										className: 'gutenify-control-tabs-tab',
									},
								]
								: [] ),
							{
								name: 'blocks',
								title: (
									<Tooltip
										text={ __(
											'Simple blocks to construct your page.',
											'@@text_domain'
										) }
									>
										<span>{ __( 'Blocks', '@@text_domain' ) }</span>
									</Tooltip>
								),
								className: 'gutenify-control-tabs-tab',
							},
							{
								name: 'pages',
								title: (
									<Tooltip
										text={ __(
											'Pre-designed ready to use pages.',
											'@@text_domain'
										) }
									>
										<span>{ __( 'Pages', '@@text_domain' ) }</span>
									</Tooltip>
								),
								className: 'gutenify-control-tabs-tab',
							},
							{
								name: 'local',
								title: (
									<Tooltip text={ __( 'My Templates.', '@@text_domain' ) }>
										<span>{ __( 'My Templates', '@@text_domain' ) }</span>
									</Tooltip>
								),
								className: 'gutenify-control-tabs-tab',
							},
						] }
					>
						{ ( tabData ) => {
							const tabType = tabData.name;
							let currentTemplates = false;
							let selectedCategory = false;
							currentTemplates = this.getTemplates( tabType );
							selectedCategory = this.getSelectedCategory( tabType );

							// if ( 'pages' === tabType ) {
							// 	return __( 'Coming Soon...', '@@text_domain' );
							// }

							return (
								<Fragment>
									{ false === currentTemplates ? (
										<div className="gutenify-plugin-templates-spinner">
											<Spinner />
										</div>
									) : (
										''
									) }
									{ currentTemplates && ! currentTemplates.length ? (
										<div>
											{ 'local' === tabType ? (
												<Fragment>
													<p
														style={ {
															marginTop: 0,
														} }
													>
														{ __( 'No templates found.', '@@text_domain' ) }
													</p>
													<ExternalLink
														className="components-button is-button is-primary"
														href={ window.gutenify_block_data.add_template_url }
													>
														{ __( 'Add Template', '@@text_domain' ) }
													</ExternalLink>
												</Fragment>
											) : (
												__( 'No templates found.', '@@text_domain' )
											) }
										</div>
									) : (
										''
									) }
									{ currentTemplates && currentTemplates.length ? (
										<Fragment key={ `${ tabType }-${ selectedCategory }` }>
											<div className="gutenify-plugin-templates-categories-row">
												<div className="gutenify-plugin-templates-categories-select">
													{ this.printCategorySelect( tabType ) }
												</div>
												<div className="gutenify-plugin-templates-count">
													<RawHTML>
														{ sprintf(
															__( 'Templates: %s', '@@text_domain' ),
															`<strong>${ currentTemplates.length }</strong>`
														) }
													</RawHTML>
												</div>
											</div>
											{ this.state.error }
											<Masonry
												className="gutenify-plugin-templates-list"
												elementType="ul"
												disableImagesLoaded={ false }
												updateOnEachImageLoad
												options={ {
													transitionDuration: 0,
												} }
											>
												{ currentTemplates.map( ( template ) => {
													let withThumb = !! template.thumbnail;

													if ( ! withThumb ) {
														template.thumbnail = `${ window.gutenify_block_data.plugin_directory_url }/assets/images/info-box-layouts/layout-1.png`;
														template.thumbnail_height = 320;
														template.thumbnail_width = 300;
														withThumb = true;
													}
													let thumbAspectRatio = false;

													if (
														template.thumbnail_height &&
														template.thumbnail_width
													) {
														thumbAspectRatio =
															template.thumbnail_height /
															template.thumbnail_width;
													}

													return (
														<li
															className={ classnames(
																'gutenify-plugin-templates-list-item',
																withThumb
																	? ''
																	: 'gutenify-plugin-templates-list-item-no-thumb'
															) }
															key={ template.id }
														>
															{ /* eslint-disable-next-line react/button-has-type */ }
															<button
																onClick={ () => {
																	this.setState( {
																		loading: true,
																	} );
																	getTemplateData(
																		{
																			id: template.id,
																			type: tabType,
																		},
																		( data ) => {
																			if (
																				data &&
																				data.success &&
																				data.response &&
																				data.response.content
																			) {
																				insertTemplate(
																					data.response.content,
																					this.props.replaceBlockId,
																					( error ) => {
																						if ( error ) {
																							this.setState( { error } );
																						} else {
																							onRequestClose();
																						}
																					}
																				);
																			}
																			this.setState( {
																				loading: false,
																			} );
																		}
																	);
																} }
															>
																{ withThumb ? (
																	<div className="gutenify-plugin-templates-list-item-image">
																		{ thumbAspectRatio ? (
																			<div
																				className="gutenify-plugin-templates-list-item-image-sizer"
																				style={ {
																					paddingTop: `${ 100 *
																						thumbAspectRatio }%`,
																				} }
																			/>
																		) : (
																			''
																		) }
																		<LazyLoad overflow offset={ 100 }>
																			<img
																				src={ template.thumbnail }
																				alt={ template.title }
																			/>
																		</LazyLoad>
																	</div>
																) : (
																	''
																) }
																<div className="gutenify-plugin-templates-list-item-title">
																	{ template.title ? template.title : __( '(No title)' ) }
																</div>
															</button>
														</li>
													);
												} ) }
											</Masonry>
											{ 'local' === tabType ? (
												<Fragment>
													<ExternalLink
														className="components-button is-button is-primary"
														href={ window.gutenify_block_data.add_template_url }
													>
														{ __( 'Add Template', '@@text_domain' ) }
													</ExternalLink>
												</Fragment>
											) : (
												''
											) }
										</Fragment>
									) : (
										''
									) }
								</Fragment>
							);
						} }
					</TabPanel>
				) : (
					''
				) }
			</Modal>
		);
	}
}

const checkMissingBlocks = () => {
	return false;
};

export default compose( [
	withDispatch( ( dispatch ) => {
		const { insertBlocks, replaceBlocks } = dispatch( 'core/block-editor' );

		return {
			insertTemplate( content, replaceBlockId, cb ) {
				const parsedBlocks = parse( content );

				if ( parsedBlocks.length ) {
					const missingBlocksData = checkMissingBlocks( parsedBlocks );

					if ( missingBlocksData ) {
						cb( missingBlocksData );
					} else {
						if ( replaceBlockId ) {
							replaceBlocks( replaceBlockId, parsedBlocks );
						} else {
							insertBlocks( parsedBlocks );
						}
						cb( false );
					}
				}
			},
		};
	} ),
	withSelect( ( select ) => {
		const templates = select( 'gutenify/templates' ).getTemplates();

		return {
			templates,
			getTemplates: ( reset = false ) => {
				return new Promise( function( resolve, reject ) {
					apiFetch( {
						path: '/gutenify/v1/get_templates/?reset=' + reset,
					} )
						.then( ( res ) => {
							resolve( res.response );
						} )
						.catch( ( error ) => {
							reject( error );
						} );
				} );
			},
			getTemplateData( data, cb ) {
				let { type } = data;
				if ( 'local' !== type && 'theme' !== type ) {
					type = 'remote';
				}

				apiFetch( {
					path: `/gutenify/v1/get_template_data/?id=${ data.id }&type=${ type }`,
					method: 'GET',
				} )
					.then( ( result ) => {
						cb( result );
					} )
					.catch( ( error ) => {
						cb( error );
					} );
			},
		};
	} ),
] )( TemplatesModal );
